/**
 * Memory files hooks — list + read + write.
 *
 * File write uses optimistic-concurrency via If-Match (HEAD sha from the
 * last GET). A 412 from the server means someone else committed — let
 * the caller surface a conflict dialog (not implemented in this v1 UI;
 * we just bubble the ApiError up).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { memoryApi } from '@/api/memory';

export function useMemoryFiles(agent: string | undefined) {
  return useQuery({
    queryKey: ['memory', agent, 'list'],
    queryFn: () => memoryApi.list(agent!),
    enabled: !!agent,
    staleTime: 10_000,
  });
}

export function useMemoryFile(agent: string | undefined, filename: string | undefined) {
  return useQuery({
    queryKey: ['memory', agent, 'file', filename],
    queryFn: () => memoryApi.get(agent!, filename!),
    enabled: !!agent && !!filename,
    staleTime: Infinity,
  });
}

export function useSaveMemoryFile(agent: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      filename,
      content,
      headSha,
    }: {
      filename: string;
      content: string;
      headSha: string;
    }) => memoryApi.put(agent, filename, content, headSha),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ['memory', agent, 'file', variables.filename] });
      qc.invalidateQueries({ queryKey: ['memory', agent, 'list'] });
    },
  });
}
